import { Message } from "primereact/message";
import * as React from "react";
import { ReportJobBatchEntry } from "../../../client/types.gen";
import { PerilDataSource } from "./bulkPerilOverride";
import styles from "./LayersCard.module.css";
import { useReportJobs } from "../rater/useIntegrationJobs";
import { isDefined } from "../../../util";

// Integration whose latest run produces the premium data for each peril source.
// In a perfect world this would be derived from the backend.
const INTEGRATION_BY_SOURCE = {
  air: ["ICM_YLT", "RATER"],
  rms: ["RMS_ELT", "RATER"],
  spatialkat: ["SPATIALKAT"],
} as const satisfies Record<PerilDataSource, string[]>;

type SourceIntegrationType =
  (typeof INTEGRATION_BY_SOURCE)[PerilDataSource][number];

const INTEGRATION_DISPLAY_NAMES: Record<SourceIntegrationType, string> = {
  RMS_ELT: "RMS",
  ICM_YLT: "ICM",
  SPATIALKAT: "KatRisk",
  RATER: "Rater",
};
const INTEGRATION_ORDER: SourceIntegrationType[] = [
  "RMS_ELT",
  "ICM_YLT",
  "SPATIALKAT",
  "RATER",
];

const STATUS_LABELS = {
  in_progress: "in progress…",
  success: "succeeded",
  failure: "failed",
  not_started: "not yet started",
} as const;
type RunStatus = keyof typeof STATUS_LABELS;

function latestRunStatus(jobs: ReportJobBatchEntry[]): RunStatus {
  if (!jobs || jobs.length === 0) return "not_started";
  const latest = jobs.reduce((a, b) =>
    new Date(b.started_at).getTime() > new Date(a.started_at).getTime() ? b : a
  );
  return latest.outcome ?? "in_progress";
}

/**
 * Banner summarizing the latest model run per integration that the selected
 * peril sources depend on: in progress, succeeded, failed, or not yet started.
 */
export const IntegrationStatusMessage: React.FC<{
  reportId: string;
  sources: PerilDataSource[];
}> = ({ reportId, sources }) => {
  const required = new Set(sources.flatMap((s) => INTEGRATION_BY_SOURCE[s]));
  const integrations = INTEGRATION_ORDER.filter((t) => required.has(t));

  const { jobs: allJobs } = useReportJobs(reportId);
  const statuses = integrations
    .map((type) => {
      const status = latestRunStatus(
        allJobs.filter((j) => j.integration_type === type)
      );
      if (status !== "success") {
        return { type, status };
      }
    })
    .filter(isDefined);

  if (statuses.length === 0) return null;

  const severity = statuses.some((s) => s.status === "failure")
    ? "error"
    : statuses.some((s) => s.status === "not_started")
      ? "warn"
      : statuses.some((s) => s.status === "in_progress")
        ? "info"
        : "success";

  const text = statuses
    .map(
      (s) => `${INTEGRATION_DISPLAY_NAMES[s.type]} ${STATUS_LABELS[s.status]}`
    )
    .join(" · ");

  return (
    <Message
      severity={severity}
      className={styles.integrationStatusMessage}
      text={`Modeling: ${text}`}
    />
  );
};
